
import { Component, Vue } from "vue-property-decorator"
import { getHoldingCompanies } from "@/helpers/holding-companies"
import * as Models from "@gigalot/data-models"

type LogRocketValue = string | number | boolean | null
type LogRocketApp = {
  app: string
  locationGuid: string
  stationGuid: string
  isNew?: boolean
  saving?: boolean
  removing?: boolean
  error?: string | null
}
type LogRocketUser = {
  uid: string
  displayName: string
  email: string
  enabled: boolean
  logRocketApps: LogRocketApp[]
}

@Component
export default class LogRocket extends Vue {
  loading: boolean = false
  search: string = ""
  error: string | null = null
  users: LogRocketUser[] = []
  holdingCompanies: Models.HoldingCompany[] = []
  headers: Array<{ text: string; value: string; sortable?: boolean }> = [
    { text: "Display Name", value: "displayName" },
    { text: "Email", value: "email" },
    { text: "Actions", value: "actions", sortable: false },
  ]
  appHeaders: Array<{ text: string; value: string; sortable?: boolean }> = [
    { text: "App", value: "app" },
    { text: "Location", value: "locationGuid" },
    { text: "Station", value: "stationGuid" },
    { text: "Actions", value: "actions", sortable: false },
  ]
  appOptions: string[] = ["office", "field", "processing", "feeder", "pilot"]
  expanded: LogRocketUser[] = []
  private readonly logRocketMutation: string = `mutation logRocketUser($uid: String!, $enabled: Boolean!, $app: String!, $locationGuid: String!, $stationGuid: String) {
    logRocketUser(uid: $uid, enabled: $enabled, app: $app, locationGuid: $locationGuid, stationGuid: $stationGuid)
  }`

  get feedlotOptions(): Array<{ text: string; value: string }> {
    return this.holdingCompanies.flatMap((company: Models.HoldingCompany) => {
      const companyName = company?.name || "Unknown company"
      const feedlots: Array<{ guid?: string; name?: string }> = (company as any)?.feedlots || []

      return feedlots
        .filter((feedlot) => typeof feedlot?.guid === "string" && feedlot.guid.length > 0)
        .map((feedlot) => ({
          text: `${companyName} • ${feedlot.name || feedlot.guid}`,
          value: feedlot.guid as string,
        }))
    })
      .sort((first, second) => first.text.localeCompare(second.text, undefined, { sensitivity: "base" }))
  }

  mounted() {
    this.fetchUsers()
  }

  async fetchUsers(expandTarget?: string | Event) {
    this.loading = true
    this.error = null

    const expandUid = typeof expandTarget === "string" ? expandTarget : undefined
    const previousExpandedUid = this.expanded.length ? this.expanded[0]?.uid : undefined

    try {
      const gql = `query logRocketUsers {
        logRocketUsers
      }`

      const response = await this.$store.dispatch("graphQl", { gql })
      const rawUsers: string | undefined = response?.data?.logRocketUsers
      const parsedUsers = this.parseUsers(rawUsers)
      this.users = parsedUsers
      if (!this.holdingCompanies.length) {
        await this.loadHoldingCompanies()
      }

      const uidToExpand = expandUid || previousExpandedUid
      if (uidToExpand) {
        const match = this.users.find((candidate) => candidate.uid === uidToExpand)
        this.expanded = match ? [match] : []
      } else if (!expandUid) {
        this.expanded = []
      }
    } catch (err) {
      console.error(err)
      this.error = "Failed to load LogRocket users."
      this.users = []
    } finally {
      this.loading = false
    }
  }

  private parseUsers(rawUsers: string | undefined): LogRocketUser[] {
    if (!rawUsers) {
      return []
    }

    let parsed: unknown
    try {
      parsed = JSON.parse(rawUsers)
    } catch (error) {
      console.error("Unable to parse logRocketUsers payload", error)
      throw new Error("Received malformed LogRocket response.")
    }

    if (!Array.isArray(parsed)) {
      throw new Error("logRocketUsers payload is not an array.")
    }

    return parsed
      .filter((user: unknown): user is Record<string, unknown> => typeof user === "object" && user !== null)
      .map((user: Record<string, unknown>) => this.normalizeUser(user))
  }

  private normalizeUser(user: Record<string, unknown>): LogRocketUser {
    const uid = typeof user["uid"] === "string" ? user["uid"] : ""
    const enabled = typeof user["enabled"] === "boolean" ? user["enabled"] : true

    return {
      uid,
      displayName: this.valueToString(this.normalizeValue("displayName", user["displayName"] ?? "")),
      email: this.valueToString(this.normalizeValue("email", user["email"] ?? "")),
      enabled,
      logRocketApps: this.normalizeApps(user["logRocketApps"]),
    }
  }

  private normalizeValue(key: string, value: unknown): LogRocketValue {
    if (value === null) {
      return null
    }

    if (typeof value === "number" || typeof value === "boolean") {
      return value
    }

    if (value instanceof Date) {
      return this.formatDate(value)
    }

    if (typeof value === "string") {
      if (key.endsWith("At") || key.toLowerCase().includes("date")) {
        return this.formatDate(value)
      }
      return value
    }

    return JSON.stringify(value)
  }

  private normalizeApps(rawApps: unknown): LogRocketApp[] {
    if (!Array.isArray(rawApps)) {
      return []
    }

    return rawApps.map((app) => this.normalizeApp(app))
  }

  private normalizeApp(app: unknown): LogRocketApp {
    if (!app || typeof app !== "object") {
      return { app: "", locationGuid: "", stationGuid: "", isNew: false, saving: false, removing: false, error: null }
    }

    const record = app as Record<string, unknown>

    return {
      app: typeof record.app === "string" ? record.app : "",
      locationGuid: typeof record.locationGuid === "string" ? record.locationGuid : "",
      stationGuid: typeof record.stationGuid === "string" ? record.stationGuid : "",
      isNew: false,
      saving: false,
      removing: false,
      error: null,
    }
  }

  private valueToString(value: LogRocketValue): string {
    if (value === null) {
      return ""
    }

    return String(value)
  }

  private formatDate(value: string | Date): string {
    const date = value instanceof Date ? value : new Date(value)
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString()
  }

  private async loadHoldingCompanies(): Promise<void> {
    try {
      this.holdingCompanies = await getHoldingCompanies()
    } catch (error) {
      console.error("Failed to fetch holding companies via GraphQL", error)
      this.holdingCompanies = []
    }
  }

  addAppRow(user: LogRocketUser): void {
    user.logRocketApps.push({
      app: "",
      locationGuid: "",
      stationGuid: "",
      isNew: true,
      saving: false,
      removing: false,
      error: null,
    })
    this.expanded = [user]
  }

  isAppRowValid(app: LogRocketApp): boolean {
    if (!app.app || !app.locationGuid) {
      return false
    }

    if (app.app === "processing" && !app.stationGuid) {
      return false
    }

    return true
  }

  getProcessingStationOptions(locationGuid: string): Array<{ text: string; value: string }> {
    if (!locationGuid) {
      return []
    }

    for (const company of this.holdingCompanies) {
      const feedlots: Array<{ guid?: string; name?: string; processingStations?: Array<{ guid?: string; description?: string }> }> =
        (company as any)?.feedlots || []
      const matchedFeedlot = feedlots.find((feedlot) => feedlot?.guid === locationGuid)

      if (matchedFeedlot) {
        const stations = matchedFeedlot.processingStations || []
        return stations
          .filter((station) => typeof station?.guid === "string" && (station.guid as string).length > 0)
          .map((station) => ({
            text: station?.description ? `${station.description}` : (station?.guid as string),
            value: station?.guid as string,
          }))
      }
    }

    return []
  }

  onAppChange(app: LogRocketApp): void {
    app.error = null

    if (app.app !== "processing") {
      app.stationGuid = ""
      return
    }

    const availableStations = this.getProcessingStationOptions(app.locationGuid)
    if (!availableStations.some((station) => station.value === app.stationGuid)) {
      app.stationGuid = ""
    }

    if (app.locationGuid && availableStations.length === 0) {
      app.error = "No processing stations available for this location."
    }
  }

  onLocationChange(app: LogRocketApp): void {
    app.error = null

    const availableStations = this.getProcessingStationOptions(app.locationGuid)
    const hasCurrentStation = availableStations.some((station) => station.value === app.stationGuid)

    if (!hasCurrentStation) {
      app.stationGuid = ""
    }

    if (app.app === "processing" && availableStations.length === 0) {
      app.error = "No processing stations available for this location."
    } else {
      app.error = null
    }
  }

  async saveApp(user: LogRocketUser, app: LogRocketApp): Promise<void> {
    app.error = null

    if (!user.uid) {
      app.error = "User ID unavailable."
      return
    }

    if (!this.isAppRowValid(app)) {
      app.error = "Complete all required fields."
      return
    }

    await this.mutateLogRocketUser(user, app, true, "saving", "Failed to add app. Please try again.")
  }

  async removeApp(user: LogRocketUser, app: LogRocketApp): Promise<void> {
    app.error = null

    if (!user.uid) {
      app.error = "User ID unavailable."
      return
    }

    if (!app.app || !app.locationGuid) {
      app.error = "Missing app data."
      return
    }

    await this.mutateLogRocketUser(user, app, false, "removing", "Failed to remove app. Please try again.")
  }

  private async mutateLogRocketUser(
    user: LogRocketUser,
    app: LogRocketApp,
    enabled: boolean,
    mode: "saving" | "removing",
    failureMessage: string
  ): Promise<void> {
    const stationGuid = app.app === "processing" ? app.stationGuid || null : null

    if (mode === "saving") {
      app.saving = true
    } else {
      app.removing = true
    }

    try {
      await this.$store.dispatch("graphQl", {
        gql: this.logRocketMutation,
        variables: {
          uid: user.uid,
          enabled,
          app: app.app,
          locationGuid: app.locationGuid,
          stationGuid,
        },
      })

      await this.fetchUsers(user.uid)
    } catch (error) {
      console.error("logRocketUser mutation failed", error)
      app.error = failureMessage
    } finally {
      if (mode === "saving") {
        app.saving = false
      } else {
        app.removing = false
      }
    }
  }
}
